import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, FileText, CheckCircle, Activity, AlertCircle, Send, TrendingDown, Shield, Bell, Clock, Settings, AlertTriangle, DollarSign, ExternalLink, ArrowRight } from 'lucide-react';
import RiskCalculator from '../../components/RiskCalculator';

const ComplianceSupportService = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    phone: '',
    email: '',
    complianceType: '',
    facilityType: '',
    projectLocation: '',
    currentReporting: '',
    reportingFrequency: '',
    emissionSources: '',
    annualEmissions: '',
    complianceHistory: '',
    timeline: '',
    specificRequirements: '',
    additionalInfo: ''
  });

  const complianceTypes = [
    { value: '', label: 'Select compliance type' },
    { value: 'emissions-inventory', label: 'Emissions Inventory' },
    { value: 'ghg-reporting', label: 'GHG Reporting (40 CFR Part 98)' },
    { value: 'facility-reports', label: 'Facility Operating Reports (FOR)' },
    { value: 'excess-emissions', label: 'Excess Emissions Reports (EER)' },
    { value: 'triennial-inventory', label: 'Triennial Inventory' },
    { value: 'other', label: 'Other Compliance Needs' }
  ];

  const facilityTypes = [
    { value: '', label: 'Select facility type' },
    { value: 'power-generation', label: 'Power Generation Facility' },
    { value: 'oil-gas', label: 'Oil & Gas Operation' },
    { value: 'military', label: 'Military Installation' },
    { value: 'seafood', label: 'Seafood Processing Plant' },
    { value: 'mining', label: 'Mining Operation' },
    { value: 'construction', label: 'Construction/Asphalt Plant' },
    { value: 'remediation', label: 'Soil Remediation Facility' },
    { value: 'geothermal', label: 'Geothermal Operation' },
    { value: 'other', label: 'Other Industrial Facility' }
  ];

  const reportingFrequencies = [
    { value: '', label: 'Select reporting frequency' },
    { value: 'annual', label: 'Annual' },
    { value: 'triennial', label: 'Triennial (Every 3 Years)' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'as-needed', label: 'As Needed' }
  ];

  const timelines = [
    { value: '', label: 'Select timeline' },
    { value: 'immediate', label: 'Immediate (Within 1 month)' },
    { value: '3-months', label: '1-3 Months' },
    { value: '6-months', label: '3-6 Months' },
    { value: '12-months', label: '6-12 Months' },
    { value: 'flexible', label: 'Flexible Timeline' }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/services/compliance-support`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        alert('Thank you! Your compliance support inquiry has been submitted successfully. We will contact you shortly.');
        // Reset form
        setFormData({
          firstName: '', lastName: '', company: '', phone: '', email: '',
          complianceType: '', facilityType: '', projectLocation: '', currentReporting: '',
          reportingFrequency: '', emissionSources: '', annualEmissions: '',
          complianceHistory: '', timeline: '', specificRequirements: '', additionalInfo: ''
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your inquiry. Please try again or contact us directly.');
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-r from-blue-900 to-blue-700 text-white py-20"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw1fHxjb21wbGlhbmNlfGVufDB8fHx8MTczMjI0NTcwMHww&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-block bg-blue-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              Compliance & Reporting
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Environmental Compliance & Reporting
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              Staying compliant in the Interior requires more than just filing paperwork. We provide comprehensive compliance management systems tailored to remote operations.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#contact-form"
                className="inline-flex items-center px-6 py-3 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors"
              >
                Request Compliance Support
                <Send className="ml-2" size={20} />
              </a>
              <a
                href="#cost-analysis"
                className="inline-flex items-center px-6 py-3 bg-white text-blue-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                View Violation Costs
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Service Grid - Royal Blue Theme */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Comprehensive Compliance Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expert compliance management to keep your facility audit-ready and violation-free
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl">
              <BarChart3 className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Emissions Inventories</h3>
              <p className="text-gray-600">
                Accurate development of Triennial Emission Inventories and fee assessments.
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl">
              <FileText className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Greenhouse Gas (GHG)</h3>
              <p className="text-gray-600">
                Mandatory reporting support for 40 CFR Part 98 (Subparts C, W, NN).
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl">
              <CheckCircle className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Compliance Reporting</h3>
              <p className="text-gray-600">
                Preparation of Facility Operating Reports (FOR) and Excess Emissions Reports (EER) to keep you audit-ready.
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl">
              <Activity className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Remote Monitoring</h3>
              <p className="text-gray-600">
                Strategies for data collection and opacity monitoring in unmanned or remote locations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Monitor & Reporting Services */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* What We Monitor */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                What We Monitor
              </h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <TrendingDown className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Emission Limits</h3>
                    <p className="text-gray-600">Track emission sources against permitted limits with real-time alerts.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <Settings className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Operating Parameters</h3>
                    <p className="text-gray-600">Monitor equipment operating conditions and maintain compliance logs.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Clock className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Reporting Deadlines</h3>
                    <p className="text-gray-600">Never miss a submission deadline with automated tracking and reminders.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Bell className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Regulatory Changes</h3>
                    <p className="text-gray-600">Stay updated on rules and requirements affecting your operations.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Testing & Inspections</h3>
                    <p className="text-gray-600">Schedule and coordinate required testing and agency inspections.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Settings className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Equipment Maintenance</h3>
                    <p className="text-gray-600">Track maintenance schedules for compliance-critical equipment.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Our Reporting Services */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Our Reporting Services
              </h2>
              
              <div className="bg-white p-8 rounded-xl shadow-md mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">We Handle All Required Reports</h3>
                <ul className="space-y-3">
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span className="text-gray-700">Annual emission inventory reports</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span className="text-gray-700">Semi-annual monitoring reports</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span className="text-gray-700">Title V compliance certifications</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span className="text-gray-700">Quarterly progress reports</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span className="text-gray-700">Special agency reports</span>
                  </li>
                </ul>
              </div>

              <div className="bg-blue-600 text-white p-8 rounded-xl">
                <h3 className="text-xl font-bold mb-4">Compliance Dashboard Access</h3>
                <p className="text-blue-100 mb-6">
                  24/7 access to your compliance status, upcoming deadlines, and historical data through our secure online portal.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center text-white font-semibold hover:text-blue-200 transition-colors"
                >
                  Request Demo Access
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cost Analysis Section */}
      <section id="cost-analysis" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              The True Cost of Violations
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Understanding what&apos;s at stake without proper compliance support
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* Direct Costs */}
            <div className="bg-red-50 border-2 border-red-200 p-8 rounded-xl">
              <AlertTriangle className="w-12 h-12 text-red-600 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Direct Costs</h3>
              <ul className="space-y-3">
                <li className="flex items-center space-x-3">
                  <DollarSign className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <span className="text-gray-700">EPA Civil Penalties</span>
                </li>
                <li className="flex items-center space-x-3">
                  <DollarSign className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <span className="text-gray-700">State Enforcement Actions</span>
                </li>
                <li className="flex items-center space-x-3">
                  <DollarSign className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <span className="text-gray-700">Legal & Court Fees</span>
                </li>
                <li className="flex items-center space-x-3">
                  <DollarSign className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <span className="text-gray-700">Corrective Actions</span>
                </li>
                <li className="flex items-center space-x-3">
                  <DollarSign className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <span className="text-gray-700">Emergency Response</span>
                </li>
              </ul>
            </div>

            {/* Indirect Costs */}
            <div className="bg-orange-50 border-2 border-orange-200 p-8 rounded-xl">
              <AlertCircle className="w-12 h-12 text-orange-600 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Indirect Costs</h3>
              <ul className="space-y-3">
                <li className="flex items-center space-x-3">
                  <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  <span className="text-gray-700">Operational Shutdowns</span>
                </li>
                <li className="flex items-center space-x-3">
                  <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  <span className="text-gray-700">Permit Revocation Risk</span>
                </li>
                <li className="flex items-center space-x-3">
                  <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  <span className="text-gray-700">Reputation Damage</span>
                </li>
                <li className="flex items-center space-x-3">
                  <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  <span className="text-gray-700">Insurance Rate Increases</span>
                </li>
                <li className="flex items-center space-x-3">
                  <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  <span className="text-gray-700">Management Distraction</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Pricing Callout */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-xl text-center">
            <h3 className="text-3xl font-bold mb-4">Our Compliance Support</h3>
            <div className="text-5xl font-bold mb-4">$2K - $5K/month</div>
            <p className="text-xl text-blue-100 mb-6">
              A fraction of a single violation penalty with ongoing protection
            </p>
            <div className="bg-white/20 backdrop-blur-sm p-6 rounded-lg inline-block">
              <div className="flex items-center justify-center space-x-3">
                <CheckCircle className="w-8 h-8 text-green-300" />
                <span className="text-lg font-semibold">
                  Prevent one violation and recover 10-100x your annual investment
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Resources */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Compliance Resources
            </h2>
            <p className="text-xl text-gray-600">
              Essential links and resources for environmental compliance in Alaska
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <FileText className="w-10 h-10 text-blue-600" />
                <ExternalLink className="w-5 h-5 text-gray-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Alaska DEC Compliance Forms</h3>
              <p className="text-gray-600 mb-4">
                Official Alaska air quality compliance forms, including emission inventories and reporting templates.
              </p>
              <a
                href="https://dec.alaska.gov/air/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-800 transition-colors"
              >
                Access Resource
                <ArrowRight className="ml-2 w-4 h-4" />
              </a>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <Shield className="w-10 h-10 text-blue-600" />
                <ExternalLink className="w-5 h-5 text-gray-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">EPA Title V Monitoring & Recordkeeping</h3>
              <p className="text-gray-600 mb-4">
                Federal monitoring, recordkeeping, and reporting requirements for Title V operating permits.
              </p>
              <a
                href="https://www.epa.gov/title-v-operating-permits"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-800 transition-colors"
              >
                Access Resource
                <ArrowRight className="ml-2 w-4 h-4" />
              </a>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <Activity className="w-10 h-10 text-blue-600" />
                <ExternalLink className="w-5 h-5 text-gray-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Alaska DEC Permittee Portal</h3>
              <p className="text-gray-600 mb-4">
                Official portal for electronic submission of compliance certifications, monitoring reports, and permit applications.
              </p>
              <a
                href="https://my.alaska.gov/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-800 transition-colors"
              >
                Access Resource
                <ArrowRight className="ml-2 w-4 h-4" />
              </a>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <CheckCircle className="w-10 h-10 text-blue-600" />
                <ExternalLink className="w-5 h-5 text-gray-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">EPA Clean Air Act Compliance</h3>
              <p className="text-gray-600 mb-4">
                Comprehensive guidance on Clean Air Act compliance including monitoring, inspection, and enforcement procedures.
              </p>
              <a
                href="https://www.epa.gov/clean-air-act-overview"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-800 transition-colors"
              >
                Access Resource
                <ArrowRight className="ml-2 w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Smart Compliance Risk Estimator */}
      <RiskCalculator />

      {/* Contact Form */}
      <section id="contact-form" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="bg-blue-700 text-white p-8 rounded-t-xl">
              <div className="flex items-center mb-4">
                <Send className="w-10 h-10 mr-4" />
                <div>
                  <h2 className="text-3xl font-bold">Request Compliance Support</h2>
                  <p className="text-blue-100 mt-2">Provide details about your compliance needs for a customized consultation</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-b-xl shadow-xl border border-gray-200">
              {/* Contact Information */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company/Organization *
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Compliance Information */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Compliance Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Compliance Type Needed
                    </label>
                    <select
                      name="complianceType"
                      value={formData.complianceType}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {complianceTypes.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Facility Type
                    </label>
                    <select
                      name="facilityType"
                      value={formData.facilityType}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {facilityTypes.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Facility Location
                    </label>
                    <input
                      type="text"
                      name="projectLocation"
                      value={formData.projectLocation}
                      onChange={handleChange}
                      placeholder="City/Region in Alaska"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reporting Frequency
                    </label>
                    <select
                      name="reportingFrequency"
                      value={formData.reportingFrequency}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {reportingFrequencies.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Technical Details */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Technical Details</h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Reporting Status
                  </label>
                  <textarea
                    name="currentReporting"
                    value={formData.currentReporting}
                    onChange={handleChange}
                    rows="2"
                    placeholder="Describe current compliance reporting status"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  ></textarea>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Emission Sources
                  </label>
                  <textarea
                    name="emissionSources"
                    value={formData.emissionSources}
                    onChange={handleChange}
                    rows="2"
                    placeholder="e.g., engines, boilers, process equipment"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  ></textarea>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Approximate Annual Emissions
                  </label>
                  <input
                    type="text"
                    name="annualEmissions"
                    value={formData.annualEmissions}
                    onChange={handleChange}
                    placeholder="e.g., NOx: 10 tons/year, PM: 5 tons/year"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Compliance History */}
              <div className="mb-8">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Compliance History
                  </label>
                  <textarea
                    name="complianceHistory"
                    value={formData.complianceHistory}
                    onChange={handleChange}
                    rows="2"
                    placeholder="Any past violations, notices, or compliance issues"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  ></textarea>
                </div>
              </div>

              {/* Timeline and Additional Info */}
              <div className="mb-8">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Timeline
                  </label>
                  <select
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {timelines.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Specific Requirements or Concerns <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="specificRequirements"
                    value={formData.specificRequirements}
                    onChange={handleChange}
                    required
                    rows="4"
                    placeholder="e.g., upcoming deadlines, specific reporting needs, compliance concerns, etc."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  ></textarea>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Information
                  </label>
                  <textarea
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Any additional details about your compliance needs or questions"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  ></textarea>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex items-center justify-center">
                <button
                  type="submit"
                  className="inline-flex items-center px-8 py-4 bg-blue-700 text-white rounded-lg font-semibold hover:bg-blue-800 transition-colors shadow-lg"
                >
                  Submit Compliance Inquiry
                  <Send className="ml-2" size={20} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Protect Your Operations Today
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Don&apos;t wait for a violation&mdash;get proactive compliance support now
          </p>
          <a
            href="#contact-form"
            className="inline-flex items-center px-8 py-4 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-all shadow-lg hover:shadow-2xl transform hover:scale-105"
          >
            Start Your Protection Plan
            <ArrowRight className="ml-2" size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default ComplianceSupportService;